import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, Lines, Reveal, EASE } from "./Motion";

export const Portrait = ({ className = "aspect-[4/5]" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <motion.div
      ref={ref}
      initial={{ clipPath: "inset(100% 0 0 0)" }}
      whileInView={{ clipPath: "inset(0% 0 0 0)" }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 1.4, ease: EASE }}
      className={`group relative overflow-hidden bg-[#121212] ${className}`}
    >
      <motion.img
        style={{ y, scale: 1.18 }}
        src="/brand/owner.jpg"
        alt="Matt Newland, owner of Newland Construction, in front of a deck he built"
        data-testid="owner-photo"
        className="h-full w-full object-cover object-[50%_25%] grayscale transition-[filter] duration-700 group-hover:grayscale-0"
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,transparent_40%,rgba(10,10,10,0.7)_100%)]" />
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5 md:p-6">
        <div className="bg-[#0A0A0A]/80 px-4 py-3 backdrop-blur-md">
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-neutral-400">Owner</div>
          <div className="mt-1 font-display text-lg font-bold text-white">Matt Newland</div>
        </div>
        <img src="/brand/newland-roofs-light.png" alt="" className="h-6 w-auto opacity-90" />
      </div>
    </motion.div>
  );
};

const FACTS = [
  ["Raised on the job", "Learned the trade as a kid, working in his dad's construction business."],
  ["Trained in the field", "Undergraduate degree in Construction Management, Master's in Business Technology."],
  ["15 years at ETSU", "Facilities Management at East Tennessee State University before going out on his own."],
];

export const Owner = () => (
  <section id="owner" data-testid="section-owner" className="relative overflow-hidden bg-[#0A0A0A] px-5 py-28 md:px-10 md:py-40">
    <div className="pointer-events-none absolute -right-[5vw] top-10 select-none whitespace-nowrap font-display text-[18vw] font-extrabold leading-none tracking-tighter text-outline">Matt Newland</div>
    <div className="relative mx-auto grid max-w-[1500px] items-center gap-16 lg:grid-cols-12">
      <div className="corner-ticks lg:col-span-5"><Portrait /></div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Eyebrow>Meet the owner</Eyebrow>
        <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter text-neutral-50 md:text-6xl">
          <Lines lines={["Hi, I'm Matt.", <span key="o" className="font-serif font-normal italic text-neutral-400">I build like it's my own home.</span>]} />
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-300 md:text-lg">
            "My knowledge of construction started when I was just a kid, working with my dad for his construction business. I decided to pursue my passion for residential construction and start my own company."
          </p>
        </Reveal>
        <div className="mt-12 border-t border-white/[0.08]">
          {FACTS.map(([t, d], i) => (
            <Reveal key={t} delay={0.1 + i * 0.08}>
              <div data-testid={`owner-promise-${i}`} className="grid grid-cols-[3rem_1fr] gap-4 border-b border-white/[0.08] py-6">
                <span className="font-mono text-xs text-neutral-500">0{i + 1}</span>
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-neutral-50 md:text-2xl">{t}</h3>
                  <p className="mt-2 text-neutral-400">{d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Link to="/about" data-testid="owner-cta-button"
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-white py-3 pl-6 pr-3 font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200">
          Read Matt's story
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A0A0A] text-white transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
        </Link>
      </div>
    </div>
  </section>
);
